import {useCallback, useMemo, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLazyQuery, gql} from '@apollo/client'
import {
  setChartVariables,
  setChartNationData,
  setLoadingChartNation,
  setErrorLoadingChartNation,
  setChartStateData,
  setLoadingChartState,
  setErrorLoadingChartState,
  setChartCountyData,
  setLoadingChartCounty,
  setErrorLoadingChartCounty,
  selectChartVariables,
  selectChartNationData,
  selectChartStateData,
  selectChartCountyData
} from '../slices/longitudinalRiskChartSlice'

const NATION_QUERY = gql`
  query heRiskNation($he_key: AlphaSpecialChar!, $min_risk: Int!, $max_risk: Int!) {
    heRiskNation(he_key: $he_key, min_risk: $min_risk, max_risk: $max_risk) {
      DATA_BRICK
      COUNT_PER_100K
    }
  }
`

const STATE_QUERY = gql`
  query heRiskState($he_key: AlphaSpecialChar!, $min_risk: Int!, $max_risk: Int!, $state_abbreviation: AlphabetOnly!) {
    heRiskState(he_key: $he_key, min_risk: $min_risk, max_risk: $max_risk, state_abbreviation: $state_abbreviation) {
      DATA_BRICK
      COUNT_PER_100K
    }
  }
`

const COUNTY_QUERY = gql`
  query heRiskCounty(
    $he_key: AlphaSpecialChar!
    $min_risk: Int!
    $max_risk: Int!
    $state_county_fips_code: NumaricOnly!
  ) {
    heRiskCounty(
      he_key: $he_key
      min_risk: $min_risk
      max_risk: $max_risk
      state_county_fips_code: $state_county_fips_code
    ) {
      DATA_BRICK
      COUNT_PER_100K
    }
  }
`

/**
 * Query hook for fetching nation-level risk chart data
 */
export function useQueryRiskNationChart(chartVariables) {
  const dispatch = useDispatch()
  const chartNationData = useSelector(selectChartNationData)

  const [getChartNationData, {data: chartData, loading: loadingChart, error: errorLoadingChart}] = useLazyQuery(
    NATION_QUERY,
    {
      context: {clientName: 'longitudinal'},
      variables: {
        he_key: chartVariables.healthKey,
        min_risk: chartVariables.minRisk,
        max_risk: chartVariables.maxRisk
      },
      errorPolicy: 'all'
    }
  )

  // Sync Apollo query results to Redux
  useEffect(() => {
    if (chartData !== undefined) {
      dispatch(setChartNationData(chartData))
    }
  }, [chartData, dispatch])

  useEffect(() => {
    dispatch(setLoadingChartNation(loadingChart))
  }, [loadingChart, dispatch])

  useEffect(() => {
    dispatch(setErrorLoadingChartNation(errorLoadingChart))
  }, [errorLoadingChart, dispatch])

  return [getChartNationData, chartNationData]
}

/**
 * Query hook for fetching state-level risk chart data
 */
export function useQueryRiskStateChart(chartVariables) {
  const dispatch = useDispatch()
  const chartStateData = useSelector(selectChartStateData)

  const [getChartStateData, {data: chartData, loading: loadingChart, error: errorLoadingChart}] = useLazyQuery(
    STATE_QUERY,
    {
      context: {clientName: 'longitudinal'},
      variables: {
        he_key: chartVariables.healthKey,
        min_risk: chartVariables.minRisk,
        max_risk: chartVariables.maxRisk,
        state_abbreviation: chartVariables.stateCode
      },
      errorPolicy: 'all'
    }
  )

  // Sync Apollo query results to Redux
  useEffect(() => {
    if (chartData !== undefined) {
      dispatch(setChartStateData(chartData))
    }
  }, [chartData, dispatch])

  useEffect(() => {
    dispatch(setLoadingChartState(loadingChart))
  }, [loadingChart, dispatch])

  useEffect(() => {
    dispatch(setErrorLoadingChartState(errorLoadingChart))
  }, [errorLoadingChart, dispatch])

  return [getChartStateData, chartStateData]
}

/**
 * Query hook for fetching county-level risk chart data
 */
export function useQueryRiskCountyChart(chartVariables) {
  const dispatch = useDispatch()
  const chartCountyData = useSelector(selectChartCountyData)

  const [getChartCountyData, {data: chartData, loading: loadingChart, error: errorLoadingChart}] = useLazyQuery(
    COUNTY_QUERY,
    {
      context: {clientName: 'longitudinal'},
      variables: {
        he_key: chartVariables.healthKey,
        min_risk: chartVariables.minRisk,
        max_risk: chartVariables.maxRisk,
        state_county_fips_code: chartVariables.stateCountyCode
      },
      errorPolicy: 'all'
    }
  )

  // Sync Apollo query results to Redux
  useEffect(() => {
    if (chartData !== undefined) {
      dispatch(setChartCountyData(chartData))
    }
  }, [chartData, dispatch])

  useEffect(() => {
    dispatch(setLoadingChartCounty(loadingChart))
  }, [loadingChart, dispatch])

  useEffect(() => {
    dispatch(setErrorLoadingChartCounty(errorLoadingChart))
  }, [errorLoadingChart, dispatch])

  return [getChartCountyData, chartCountyData]
}

/**
 * Hook for managing longitudinal risk chart state and queries
 * Replaces LongitudinalRiskChartProvider
 */
export const useLongitudinalRiskChart = () => {
  const dispatch = useDispatch()
  const chartVariables = useSelector(selectChartVariables)

  const [getChartNationData, chartNationData] = useQueryRiskNationChart(chartVariables)
  const [getChartStateData, chartStateData] = useQueryRiskStateChart(chartVariables)
  const [getChartCountyData, chartCountyData] = useQueryRiskCountyChart(chartVariables)

  const updateChartVariables = useCallback(
    (variables) => {
      dispatch(setChartVariables(variables))
    },
    [dispatch]
  )

  return useMemo(
    () => ({
      getChartNationData,
      chartNationData,
      getChartStateData,
      chartStateData,
      getChartCountyData,
      chartCountyData,
      setChartVariables: updateChartVariables
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chartNationData.chartData,
      chartNationData.loadingChart,
      chartNationData.errorLoadingChart,
      chartStateData.chartData,
      chartStateData.loadingChart,
      chartStateData.errorLoadingChart,
      chartCountyData.chartData,
      chartCountyData.loadingChart,
      chartCountyData.errorLoadingChart,
      updateChartVariables
    ]
  )
}
